import type { RefundProtectQuoteRequest } from 'api/insurance'
import { SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import { getChangeOfMindBeforeCheckInDays, getFlexibleCancellationDays, isFlexibleCancellationPolicy, isWithinChangeOfMindPolicy } from 'lib/offer/cancellationPolicyUtils'
import moment from 'moment'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import getObjectKey from 'lib/object/getObjectKey'
import { getCurrentIntl } from 'i18n/util/currentIntl'

interface CancellationPolicyInfo {
  cancellationType?: 'free' | 'flexible'
  cancelUntilFullRefundDate: moment.Moment
  cancelAfterNonRefundableDate?: moment.Moment
  cancellationAfterChangeOfMindPeriod: boolean
}

export function getBookingProtectionQuoteKey(quotePayload: RefundProtectQuoteRequest | null): string {
  if (!quotePayload) {
    return 'default'
  }

  return getObjectKey(quotePayload)
}

function showFreeCancellation(
  checkInDate?: string | moment.Moment,
  cancellationPolicy?: App.OfferCancellationPolicy,
) {
  if (!cancellationPolicy) {
    return false
  }
  if (cancellationPolicy.type !== 'post-purchase-seven-days') {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    return moment(checkInDate).subtract(21, 'days').isAfter(moment().startOf('day'))
  }
}

function showFlexibleCancellation(
  checkInDate?: string,
  cancellationPolicy?: App.OfferCancellationPolicy,
) {
  if (!cancellationPolicy) {
    return false
  }
  if (!isFlexibleCancellationPolicy(cancellationPolicy)) {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    const days = getFlexibleCancellationDays(cancellationPolicy.type)
    const withinChangeOfMind = isWithinChangeOfMindPolicy(new Date(), checkInDate, cancellationPolicy.type)
    return withinChangeOfMind || moment(checkInDate).subtract(days, 'days').isAfter(moment().startOf('day'))
  }
}

export function isBookingProtectionAvailableForAccommodationOffer(offer: App.AnyOffer): boolean {
  if (offer.parentType === OFFER_TYPE_HOTEL) {
    if (offer.type === OFFER_TYPE_ALWAYS_ON) return true
    if (offer.type === OFFER_TYPE_HOTEL) return true
    if (offer.type === OFFER_TYPE_BED_BANK) return true
  }

  return false
}

export function getCancellationPolicyInfo(
  checkInDate?: string,
  cancellationPolicy?: App.OfferCancellationPolicy,
): CancellationPolicyInfo {
  const freeCancellation = showFreeCancellation(checkInDate, cancellationPolicy)
  const flexibleCancellation = showFlexibleCancellation(checkInDate, cancellationPolicy)
  const cancellationType = freeCancellation ? 'free' : flexibleCancellation ? 'flexible' : undefined
  let startBasedOnToday: moment.Moment = moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let cancelUntilFullRefundDate: moment.Moment = moment()
  let cancelAfterNonRefundableDate: moment.Moment | undefined
  let cancellationAfterChangeOfMindPeriod = false

  if (freeCancellation) {
    const startBasedOnCheckIn = checkInDate && moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days')
    if (startBasedOnCheckIn) {
      cancelUntilFullRefundDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
    }
  } else if (flexibleCancellation && cancellationPolicy && checkInDate) {
    const days = getFlexibleCancellationDays(cancellationPolicy.type)
    let startBasedOnCheckIn = moment(checkInDate).subtract(days, 'days')

    const changeOfMindDeadline = moment(checkInDate).subtract(getChangeOfMindBeforeCheckInDays(cancellationPolicy.type), 'days')
    startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

    cancellationAfterChangeOfMindPeriod = startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days'))
    startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

    cancelUntilFullRefundDate = startBasedOnToday
    cancelAfterNonRefundableDate = startBasedOnCheckIn
  }

  return {
    cancellationType,
    cancelUntilFullRefundDate,
    cancelAfterNonRefundableDate,
    cancellationAfterChangeOfMindPeriod,
  }
}

export function getBookingProtectionCancellationReasons(regionCode?: string) {
  const intl = getCurrentIntl()
  const shouldHideGbUnsupportedReasons = regionCode === 'GB'

  return [
    intl.formatMessage({ id: 'a2Z9Qd9jy4aIgcqmlbHb', defaultMessage: 'Illness/injury (including Covid)' }),
    intl.formatMessage({ id: 'ABBKcwS9_p8C04FbRWC5', defaultMessage: 'Home emergency' }),
    intl.formatMessage({ id: 'e_6IrUm6CwqEKuTiG2Yl', defaultMessage: 'Pre-existing medical condition' }),
    ...(!shouldHideGbUnsupportedReasons ? [intl.formatMessage({ id: 'VQ5X7PvFEdjrAoe_LaSL', defaultMessage: 'Flight disruption' })] : []),
    intl.formatMessage({ id: 'H7T2V9pbXmgUotXSVnHb', defaultMessage: 'Adverse weather' }),
    intl.formatMessage({ id: '74gUdjHg-GDN6G-wmw4w', defaultMessage: 'Theft of documents' }),
    intl.formatMessage({ id: 'dWMPjWGAaopgB1g767Af', defaultMessage: 'Workplace redundancy' }),
    intl.formatMessage({ id: 'NzGEDY9PV73qgitm1eLx', defaultMessage: 'Jury service' }),
    intl.formatMessage({ id: 'g7BwWhak8cv0aoDUN7Hd', defaultMessage: 'Court summons' }),
    intl.formatMessage({ id: 'XHlDYFOdoZhVHRyWt3mi', defaultMessage: 'Pregnancy complications' }),
    intl.formatMessage({ id: 'Czvdfz5C_GEchjJktX0n', defaultMessage: 'Death of immediate family member' }),
    intl.formatMessage({ id: 'N-yRj7jkpwsgwjweRvtL', defaultMessage: 'Mechanical breakdown' }),
    intl.formatMessage({ id: 'aiajSR0MdBc9qKXFp4RC', defaultMessage: 'Relocated for work' }),
    intl.formatMessage({ id: 'Y9Z1o-izSN8mkOVmO6Fs', defaultMessage: 'Armed forces & emergency services recall' }),
    ...(!shouldHideGbUnsupportedReasons ? [intl.formatMessage({ id: 'K1eTr26z83u_K4eGiKm7', defaultMessage: 'Changes to examination dates' })] : []),
  ]
}

export function getFlightsBookingProtectionCancellationReasons() {
  const intl = getCurrentIntl()
  return [
    intl.formatMessage({ id: 'a2Z9Qd9jy4aIgcqmlbHb', defaultMessage: 'Illness/injury (including Covid)' }),
    intl.formatMessage({ id: 'e_6IrUm6CwqEKuTiG2Yl', defaultMessage: 'Pre-existing medical condition' }),
    intl.formatMessage({ id: 'g7BwWhak8cv0aoDUN7Hd', defaultMessage: 'Court summons' }),
    intl.formatMessage({ id: 'dWMPjWGAaopgB1g767Af', defaultMessage: 'Workplace redundancy' }),
    intl.formatMessage({ id: 'Czvdfz5C_GEchjJktX0n', defaultMessage: 'Death of immediate family member' }),
    intl.formatMessage({ id: 'gw4iOB6T5yyR3NY1gT1D', defaultMessage: 'Public transport failure' }),
    intl.formatMessage({ id: 'aiajSR0MdBc9qKXFp4RC', defaultMessage: 'Relocated for work' }),
    intl.formatMessage({ id: 'Y9Z1o-izSN8mkOVmO6Fs', defaultMessage: 'Armed forces & emergency services recall' }),
    intl.formatMessage({ id: 'ABBKcwS9_p8C04FbRWC5', defaultMessage: 'Home emergency' }),
    intl.formatMessage({ id: '74gUdjHg-GDN6G-wmw4w', defaultMessage: 'Theft of documents' }),
    intl.formatMessage({ id: 'XHlDYFOdoZhVHRyWt3mi', defaultMessage: 'Pregnancy complications' }),
    intl.formatMessage({ id: 'NzGEDY9PV73qgitm1eLx', defaultMessage: 'Jury service' }),
    intl.formatMessage({ id: 'VQ5X7PvFEdjrAoe_LaSL', defaultMessage: 'Flight disruption' }),
    intl.formatMessage({ id: 'N-yRj7jkpwsgwjweRvtL', defaultMessage: 'Mechanical breakdown' }),
    intl.formatMessage({ id: 'H7T2V9pbXmgUotXSVnHb', defaultMessage: 'Adverse weather' }),
    intl.formatMessage({ id: 'K1eTr26z83u_K4eGiKm7', defaultMessage: 'Changes to examination dates' }),
  ]
}
